import { useEffect } from "react";
import { ShieldCheck, Mail, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import GulfHeaderNav from "../../components/GulfHeaderNav";
import Logo from "../../components/Logo";
import { trackVisit } from "../../lib/api";

export default function GulfRefund() {
  useEffect(() => { trackVisit("gulf-refund"); }, []);

  return (
    <div className="min-h-screen bg-[#040914] text-white" dir="rtl" data-testid="gulf-refund-root">
      <GulfHeaderNav variant="static" />

      <section className="py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-3 text-center">سياسة الاسترجاع</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black text-center leading-tight">
            ضمان <span className="text-[#C5A059]">7 أيام</span> لاسترداد كامل
          </h1>
          <p className="mt-5 text-sm sm:text-lg text-[#A1B0C6] leading-relaxed text-center">
            هدفنا أن تستفيد فعلاً من الكتاب. إن شعرت أن المحتوى غير مناسب لك خلال أول 7 أيام من تاريخ الشراء، نُعيد إليك المبلغ كاملاً بدون أي أسئلة.
          </p>

          <div className="mt-9 sm:mt-12 space-y-7 sm:space-y-9">
            <div className="bg-[#0B1D3A]/60 border border-[#C5A059]/20 rounded-sm p-5 sm:p-7" data-testid="refund-rules">
              <div className="flex items-center gap-2 mb-3 text-[#C5A059]">
                <ShieldCheck className="w-5 h-5" />
                <h2 className="font-display text-lg sm:text-xl font-bold">شروط الاسترجاع</h2>
              </div>
              <ul className="space-y-2.5 text-sm sm:text-base text-white/90 leading-relaxed">
                <li className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/> أن يُقدَّم الطلب خلال <span className="text-[#C5A059] font-bold">7 أيام</span> كحدّ أقصى من تاريخ إتمام عملية الدفع.</li>
                <li className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/> أن يُرسَل الطلب من نفس البريد الإلكتروني المستخدم في الشراء.</li>
                <li className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/> ذكر رقم الطلب أو إرفاق إيصال الدفع.</li>
                <li className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/> تتمّ عملية الاسترجاع بنفس وسيلة الدفع الأصلية.</li>
              </ul>
            </div>

            <div className="bg-[#0B1D3A]/60 border border-[#C5A059]/20 rounded-sm p-5 sm:p-7" data-testid="refund-process">
              <h2 className="font-display text-lg sm:text-xl font-bold text-[#C5A059] mb-3">طريقة طلب الاسترجاع</h2>
              <ol className="space-y-2.5 text-sm sm:text-base text-white/90 leading-relaxed list-decimal pr-5">
                <li>أرسل رسالة إلى البريد الموضّح أدناه بعنوان "طلب استرجاع".</li>
                <li>اذكر رقم الطلب، تاريخ الشراء، والبريد الإلكتروني المستخدم.</li>
                <li>يصلك ردّ خلال 24 ساعة عمل لتأكيد استلام الطلب.</li>
                <li>يُنفَّذ الاسترجاع خلال 3–7 أيام عمل بحسب البنك ووسيلة الدفع.</li>
              </ol>
            </div>

            <div className="bg-[#0B1D3A]/60 border border-[#C5A059]/20 rounded-sm p-5 sm:p-7" data-testid="refund-exclusions">
              <h2 className="font-display text-lg sm:text-xl font-bold text-[#C5A059] mb-3">حالات لا يشملها الاسترجاع</h2>
              <ul className="space-y-2.5 text-sm sm:text-base text-white/90 leading-relaxed">
                <li className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/> تجاوز فترة الـ 7 أيام من تاريخ الشراء.</li>
                <li className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/> إعادة بيع المحتوى، أو إعادة توزيعه، أو مشاركته مع طرف ثالث.</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#C5A059]/15 via-[#C5A059]/8 to-transparent border border-[#C5A059]/40 rounded-sm p-5 sm:p-7" data-testid="refund-contact">
              <div className="flex items-center gap-2 mb-3 text-[#C5A059]">
                <Mail className="w-5 h-5" />
                <h2 className="font-display text-lg sm:text-xl font-bold">للتواصل والاسترجاع</h2>
              </div>
              <p className="text-sm sm:text-base text-white/90 leading-relaxed">
                أرسل رسالتك على البريد:
                <a href="mailto:support@bluebook.cx" className="block mt-2 text-[#C5A059] text-base sm:text-lg font-bold underline underline-offset-4" data-testid="refund-contact-email">
                  support@bluebook.cx
                </a>
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/" data-testid="refund-back-to-home">
              <Button className="bg-transparent border-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] font-bold rounded-sm px-6 py-5">
                العودة للصفحة الرئيسية
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#C5A059]/15 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1B0C6]">
          <Logo className="h-9 w-9" />
          <div>© {new Date().getFullYear()} الكتاب الأزرق</div>
          <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#C5A059]" /> دفع آمن</div>
        </div>
      </footer>
    </div>
  );
}
